import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppRouter } from "./router";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, useState } from "react";
import { useAuthStore } from "./stores/auth.store";
import { api } from "./lib/api";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Helper to decode JWT and check expiration
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const exp = payload.exp * 1000;
    return Date.now() >= exp;
  } catch {
    return true;
  }
};

function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { setLoading, accessToken, refreshToken, logout, isAuthenticated } =
    useAuthStore();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      // If no tokens stored, just set loading to false
      if (!accessToken && !refreshToken) {
        setLoading(false);
        setInitialized(true);
        return;
      }

      // If we have an access token that's still valid, we're good
      if (accessToken && !isTokenExpired(accessToken)) {
        setLoading(false);
        setInitialized(true);
        return;
      }

      // If access token is expired but we have a refresh token, try to refresh
      if (refreshToken && !isTokenExpired(refreshToken)) {
        try {
          const success = await api.forceRefreshToken();
          if (!success) {
            // Refresh failed, clear auth state
            logout();
          }
        } catch {
          // Refresh failed, clear auth state
          logout();
        }
      } else if (refreshToken && isTokenExpired(refreshToken)) {
        // Refresh token is also expired, logout
        logout();
      }

      setLoading(false);
      setInitialized(true);
    };

    initAuth();
  }, []); // Run once on mount

  // Don't render children until auth is initialized
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthInitializer>
        <AppRouter />
        <Toaster position="top-right" />
      </AuthInitializer>
    </QueryClientProvider>
  );
}

export default App;
